import { AbstractMedia } from 'types/AbstractMedia';

export type CachedMediaType = Partial<AbstractMedia> & Pick<AbstractMedia, 'path'>;

// Cache id version may be updated in case of issue but not too often to avoid filling the memory
const CACHE_ID = 'fleetback-cache-v1';

class CachedMedia {
  private media: CachedMediaType;

  private file: Promise<Blob>;

  private storage: Promise<Cache>;

  constructor (media: CachedMediaType) {
    this.media = media;

    this.initStorage();
    this.initMedia();
  }

  private initStorage () {
    this.storage = caches.open(CACHE_ID);
  }

  private initMedia () {
    this.file = this.storage.then(async (cache) => {
      let file;
      const cachedResponse = await cache.match(this.media.path, { ignoreSearch: true });
      if (cachedResponse) {
        file = cachedResponse.clone().blob();
      } else {
        const response = await fetch(this.media.path);
        cache.put(this.media.path, response.clone());
        file = response.clone().blob();
      }

      return file;
    });
  }

  public async getAsBlob (): Promise<Blob> {
    return this.file;
  }

  public async getDataUrl (): Promise<string> {
    const file = await this.file;
    return new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.addEventListener('load', () => resolve(reader.result as string));
      reader.readAsDataURL(file);
    });
  }

  public async getAsAbstractMedia (): Promise<AbstractMedia> {
    const dataUrl = await this.getDataUrl();

    return {
      ...this.media as AbstractMedia,
      path: dataUrl
    };
  }
}

export default CachedMedia;